import { Injectable } from '@angular/core';
import { type Schema } from '../../../../amplify/data/resource';
import { generateClient } from 'aws-amplify/api';
import { PhaseModel, Project, ProjectStatus, ProjectStage } from '../models/project.model';
import { BehaviorSubject, Subscription } from 'rxjs';
import { Task } from '../models/task.model';
import { User } from '../models/user.model';

@Injectable({
  providedIn: 'root',
})

export class ProjectService {
  private _client?: ReturnType<typeof generateClient<Schema>>;

  private get client() {
    if (!this._client) {
      this._client = generateClient<Schema>();
    }
    return this._client;
  }

  private projectsSubject = new BehaviorSubject<Project[]>([]);
  projects$ = this.projectsSubject.asObservable();
  private projectsStageSubject = new BehaviorSubject<ProjectStage[]>([]);
  projectsStage$ = this.projectsStageSubject.asObservable();
  private phaseModelSubject = new BehaviorSubject<PhaseModel[]>([]);
  phaseModel$ = this.phaseModelSubject.asObservable();

  // Real-time update with observeQuery (no polling needed)
  private isRefreshPaused = false; // Flag to pause refresh during editing

  // Tenant filter for server-side scoping
  private tenantFilter: { tenantId: { eq: string } } | { or: { tenantId: { eq: string } }[] } | undefined;
  public activeTenantIds: string[] | null = null;
  private projectObserverSub?: Subscription;

  // Cache for project managers and members to avoid redundant API calls
  private managersCache = new Map<string, { managers: any[], timestamp: number }>();
  private membersCache = new Map<string, { members: any[], timestamp: number }>();
  private readonly CACHE_TTL_MS = 30000; // Cache for 30 seconds

  constructor() {
    // Don't call data methods in constructor - they should be called after Amplify is configured
    // Call these methods explicitly when needed (e.g., after authentication)
  }

  /** Returns the current projects synchronously (snapshot of BehaviorSubject). */
  getProjectsSnapshot(): Project[] {
    return this.projectsSubject.value;
  }

  async init() {
    // Use observeQuery for real-time updates instead of manual polling
    this.startRealTimeObservers();

    // Initial load with parallel execution
    await Promise.all([
      this.listProjects(),
      this.listProjectsStage(),
      this.listAllPhaseList()
    ]);
  }

  /**
   * Set a server-side tenant filter for projects.
   * Pass tenant IDs for non-admin users, or null/empty to show all (admin).
   */
  setTenantFilter(tenantIds: string[] | null) {
    this.activeTenantIds = tenantIds;
    if (!tenantIds || tenantIds.length === 0) {
      this.tenantFilter = undefined;
    } else if (tenantIds.length === 1) {
      this.tenantFilter = { tenantId: { eq: tenantIds[0] } };
    } else {
      this.tenantFilter = { or: tenantIds.map(id => ({ tenantId: { eq: id } })) };
    }
    console.log('🔒 [ProjectService] Tenant filter set:', this.tenantFilter);
    // Restart project observer with new filter
    this.restartProjectObserver();
    // Re-fetch projects list with new filter
    this.listProjects();
  }

  private restartProjectObserver() {
    // Unsubscribe from previous observer
    this.projectObserverSub?.unsubscribe();
    this.startProjectObserver();
  }

  private startProjectObserver() {
    const queryOptions: any = {
      selectionSet: [
        'id',
        'projectName',
        'startDate',
        'order',
        'estimatedHours',
        'status',
        'archivedAt',
        'tenantId',
        'links',
        'clientId',
        'client.id',
        'client.name',
        'tenant.id',
        'tenant.name',
        'managers.id',
        'managers.userId',
        'managers.user.id',
        'managers.user.name',
        'managers.user.firstName',
        'managers.user.email',
        'managers.user.isArchived',
        'members.id',
        'members.userId',
        'members.user.id',
        'members.user.name',
        'members.user.firstName',
        'members.user.email',
        'members.user.isArchived',
        'isDeleted'
      ]
    };
    // Always exclude soft-deleted; optionally merge with tenant filter
    const softDeleteFilter = { isDeleted: { ne: true } };
    if (this.tenantFilter) {
      queryOptions.filter = { and: [softDeleteFilter, this.tenantFilter] };
    } else {
      queryOptions.filter = softDeleteFilter;
    }
    this.projectObserverSub = this.client.models.Project.observeQuery(queryOptions).subscribe({
      next: ({ items, isSynced }) => {
        const projects: Project[] = (items as any[]).map((p: any) => ({
          projectId: p.id,
          startDate: p.startDate ?? undefined,
          projectName: p.projectName,
          estimatedHours: (p as any).estimatedHours ?? 0,
          status: (p as any).status ?? null,
          archivedAt: (p as any).archivedAt ?? null,
          tenantId: p.tenantId,
          links: p.links?.filter((link: any) => link !== null).map((link: any) =>
            typeof link === 'string' ? JSON.parse(link) : link
          ) ?? undefined,
          order: p.order ?? 0,
          clientId: (p as any).clientId ?? undefined,
          client: (p as any).client ? { id: (p as any).client.id, name: (p as any).client.name } : undefined,
          tenant: p.tenant ? { id: p.tenant.id, name: p.tenant.name } : undefined,
          managers: p.managers?.map((pm: any) => ({
            id: pm.id,
            projectId: p.id,
            userId: pm.userId,
            user: pm.user ? {
              id: pm.user.id,
              name: pm.user.name,
              firstName: pm.user.firstName ?? undefined,
              email: pm.user.email ?? undefined,
              isArchived: pm.user.isArchived ?? false
            } : undefined
          })),
          members: p.members?.map((pm: any) => ({
            id: pm.id,
            projectId: p.id,
            userId: pm.userId,
            user: pm.user ? {
              id: pm.user.id,
              name: pm.user.name,
              firstName: pm.user.firstName ?? undefined,
              email: pm.user.email ?? undefined,
              isArchived: pm.user.isArchived ?? false
            } : undefined
          }))
        })) as Project[];

        this.projectsSubject.next(projects);

        if (isSynced) {
          console.log('✅ [Projects] Real-time sync completed');
        }
      },
      error: (error) => console.error('❌ [Projects] Real-time subscription error:', error)
    });
  }

  // Start real-time observers using Amplify's observeQuery
  startRealTimeObservers() {
    // Observe projects in real-time (uses tenant filter if set)
    this.startProjectObserver();

    // Subscribe to ProjectManager create events
    this.client.models.ProjectManager.onCreate().subscribe({
      next: (data) => {
        console.log('🔄 [ProjectManager] Manager added, refreshing projects...', data);
        this.refreshSingleProject(data.projectId);
      },
      error: (error) => console.error('❌ [ProjectManager.onCreate] Subscription error:', error)
    });

    // Subscribe to ProjectManager delete events
    this.client.models.ProjectManager.onDelete().subscribe({
      next: (data) => {
        console.log('🔄 [ProjectManager] Manager removed, refreshing projects...', data);
        this.refreshSingleProject(data.projectId);
      },
      error: (error) => console.error('❌ [ProjectManager.onDelete] Subscription error:', error)
    });

    // Subscribe to ProjectMember create events
    this.client.models.ProjectMember.onCreate().subscribe({
      next: (data) => {
        console.log('🔄 [ProjectMember] Member added, refreshing projects...', data);
        this.refreshSingleProject(data.projectId);
      },
      error: (error) => console.error('❌ [ProjectMember.onCreate] Subscription error:', error)
    });

    // Subscribe to ProjectMember delete events
    this.client.models.ProjectMember.onDelete().subscribe({
      next: (data) => {
        console.log('🔄 [ProjectMember] Member removed, refreshing projects...', data);
        this.refreshSingleProject(data.projectId);
      },
      error: (error) => console.error('❌ [ProjectMember.onDelete] Subscription error:', error)
    });

    // Observe project stages in real-time
    this.client.models.ProjectStage.observeQuery({
      filter: { isDeleted: { ne: true } },
      selectionSet: ['id', 'projectId', 'stageName', 'color', 'startDate', 'endDate', 'status', 'order', 'estimatedHours', 'links', 'isDeleted']
    }).subscribe({
      next: ({ items, isSynced }) => {
        const projectsStage: ProjectStage[] = items.map(ps => ({
          projectStageId: ps.id,
          projectId: ps.projectId,
          stageName: ps.stageName,
          color: ps.color,
          startDate: ps.startDate,
          endDate: ps.endDate,
          status: ps.status,
          order: ps.order ?? 0,
          estimatedHours: ps.estimatedHours ?? 0,
          links: ps.links?.filter((link: any) => link !== null).map((link: any) =>
            typeof link === 'string' ? JSON.parse(link) : link
          ) ?? undefined,
        }));

        // Sort by order to ensure consistent display and match server-side index
        projectsStage.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        this.projectsStageSubject.next(projectsStage);

        if (isSynced) {
          console.log('✅ [ProjectStages] Real-time sync completed');
        }
      },
      error: (error) => console.error('❌ [ProjectStages] Real-time subscription error:', error)
    });
  }

  // Refresh a single project when managers/members change
  private async refreshSingleProject(projectId: string) {
    try {
      const { data, errors } = await this.client.models.Project.get(
        { id: projectId },
        {
          selectionSet: [
            'id',
            'projectName',
            'startDate',
            'order',
            'estimatedHours',
            'status',
            'archivedAt',
            'tenantId',
            'links',
            'clientId',
            'client.id',
            'client.name',
            'tenant.id',
            'tenant.name',
            'managers.id',
            'managers.userId',
            'managers.user.id',
            'managers.user.name',
            'managers.user.firstName',
            'managers.user.email',
            'managers.user.isArchived',
            'members.id',
            'members.userId',
            'members.user.id',
            'members.user.name',
            'members.user.firstName',
            'members.user.email',
            'members.user.isArchived',
        'isDeleted'
          ]
        }
      );

      if (errors && errors.length) {
        console.error('❌ Error refreshing project:', errors);
        return;
      }

      if (data) {
        const updatedProject: Project = {
          projectId: data.id,
          startDate: data.startDate ?? undefined,
          projectName: data.projectName,
          estimatedHours: (data as any).estimatedHours ?? 0,
          status: (data as any).status ?? null,
          archivedAt: (data as any).archivedAt ?? null,
          tenantId: data.tenantId,
          links: data.links?.filter((link: any) => link !== null).map((link: any) =>
            typeof link === 'string' ? JSON.parse(link) : link
          ) ?? undefined,
          order: data.order ?? 0,
          clientId: (data as any).clientId ?? undefined,
          client: (data as any).client ? { id: (data as any).client.id, name: (data as any).client.name } : undefined,
          tenant: data.tenant ? { id: data.tenant.id, name: data.tenant.name } : undefined,
          managers: data.managers?.map((pm: any) => ({
            id: pm.id,
            projectId: data.id,
            userId: pm.userId,
            user: pm.user ? {
              id: pm.user.id,
              name: pm.user.name,
              firstName: pm.user.firstName ?? undefined,
              email: pm.user.email ?? undefined,
              isArchived: pm.user.isArchived ?? false
            } : undefined
          })),
          members: data.members?.map((pm: any) => ({
            id: pm.id,
            projectId: data.id,
            userId: pm.userId,
            user: pm.user ? {
              id: pm.user.id,
              name: pm.user.name,
              firstName: pm.user.firstName ?? undefined,
              email: pm.user.email ?? undefined,
              isArchived: pm.user.isArchived ?? false
            } : undefined
          }))
        };

        // Update the project in the existing projects array
        const currentProjects = this.projectsSubject.value;
        const index = currentProjects.findIndex(p => p.projectId === projectId);

        if (index !== -1) {
          currentProjects[index] = updatedProject;
          this.projectsSubject.next([...currentProjects]);
          console.log('✅ [refreshSingleProject] Updated project:', projectId);
        } else {
          // Project not found, might be new, add it
          this.projectsSubject.next([...currentProjects, updatedProject]);
          console.log('✅ [refreshSingleProject] Added new project:', projectId);
        }
      }
    } catch (error) {
      console.error('❌ Failed to refresh project:', error);
    }
  }

  // Pause and resume methods kept for backward compatibility
  pauseRefresh() {
    this.isRefreshPaused = true;
  }

  resumeRefresh() {
    this.isRefreshPaused = false;
  }

  stopRealTimeRefresh() {
    // No-op with observeQuery - subscriptions managed automatically
  }

  // Observe stages for a specific project - utilizes the ordered index server-side
  observeStagesForProject(projectId: string) {
    return this.client.models.ProjectStage.observeQuery({
      filter: { projectId: { eq: projectId }, isDeleted: { ne: true } }
    });
  }

  // PROJECTS
  async createProject(project: { projectName: string; startDate: string; tenantId: string; estimatedHours?: number; status?: ProjectStatus }) {
    try {
      if (!project.startDate) {
        console.error('Start date is required');
        throw new Error('Start date is required');
      }
      if (!project.tenantId) {
        console.error('Tenant is required');
        throw new Error('Please select a tenant. Create one in the Admin section if none exist.');
      }
      const { data, errors } = await this.client.models.Project.create({
        projectName: project.projectName,
        startDate: project.startDate,
        tenantId: project.tenantId,
        estimatedHours: project.estimatedHours ?? 0,
        status: project.status ?? 'ACTIVE'
      });
      if (errors && errors.length) {
        console.error('Errors creating project', errors);
        throw new Error(errors[0].message);
      }
      console.log('Project created', data);
      this.listProjects();
      return data;
    } catch (e) {
      console.error('Failed when creating the project', e);
      throw e;
    }
  }

  /**
   * Move a project along its lifecycle. Archiving stamps `archivedAt` so the list
   * can say when it was put away; any other status clears the stamp.
   */
  async setProjectStatus(projectId: string, status: ProjectStatus) {
    return this.updateProject(projectId, {
      status,
      archivedAt: status === 'ARCHIVED' ? new Date().toISOString() : null,
    });
  }

  async updateProject(projectId: string, updates: Partial<Project>) {
    try {
      // Build update object explicitly
      const updateInput: any = { id: projectId };

      if (updates.projectName !== undefined) updateInput.projectName = updates.projectName;
      if (updates.startDate !== undefined) updateInput.startDate = updates.startDate;
      if (updates.order !== undefined) updateInput.order = updates.order;
      if (updates.tenantId !== undefined) updateInput.tenantId = updates.tenantId;
      if (updates.estimatedHours !== undefined) updateInput.estimatedHours = updates.estimatedHours;
      if (updates.status !== undefined) updateInput.status = updates.status;
      if (updates.archivedAt !== undefined) updateInput.archivedAt = updates.archivedAt;
      if ('clientId' in updates) updateInput.clientId = updates.clientId || null;

      // Handle links as JSON array - stringify each object for Amplify
      if (updates.links !== undefined) {
        updateInput.links = updates.links.map((link: any) => JSON.stringify(link));
        console.log('📝 [updateProject] Links after stringify:', updateInput.links);
      }

      console.log('📝 [updateProject] Final update input:', updateInput);

      const { data, errors } = await this.client.models.Project.update(updateInput);

      if (errors && errors.length) {
        console.error('❌ Errors updating project', errors);
        return null;
      }
      console.log('✅ Project updated successfully:', data);
      this.listProjects();
      return data;
    } catch (e) {
      console.error('❌ Failed to update the project', e);
      return null;
    }
  }

  async deleteProject(projectId: string) {
    try {
      const now = new Date().toISOString();
      // Soft-delete the project
      const { data, errors } = await this.client.models.Project.update({
        id: projectId,
        isDeleted: true,
        deletedAt: now
      });
      if (errors && errors.length) {
        console.error('Errors while soft-deleting project', errors);
        return null;
      }
      // Cascade: soft-delete all stages and their tasks
      const stages = this.projectsStageSubject.value.filter(s => s.projectId === projectId);
      for (const stage of stages) {
        await this.deleteProjectStage(stage.projectStageId!);
      }
      console.log('Project soft-deleted', data);
      this.listProjects();
      return data;
    } catch (e) {
      console.error('Failed to soft-delete the project', e);
      return null;
    }
  }

  // PROJECT MANAGERS
  async addManager(projectId: string, userId: string) {
    try {
      // Check if manager already exists
      const existing = await this.client.models.ProjectManager.list({
        filter: {
          and: [
            { projectId: { eq: projectId } },
            { userId: { eq: userId } }
          ]
        }
      });

      if (existing.data && existing.data.length > 0) {
        console.warn('User is already a manager of this project');
        return existing.data[0];
      }

      const { data, errors } = await this.client.models.ProjectManager.create({
        projectId,
        userId
      });

      if (errors && errors.length) {
        console.error('Error adding manager:', errors);
        throw new Error(errors[0].message);
      }

      // Clear cache for this project
      this.clearManagerCache(projectId);

      // Real-time subscription will handle the refresh automatically
      console.log('✅ Manager added, waiting for real-time update...');
      return data;
    } catch (e) {
      console.error('Failed to add manager:', e);
      throw e;
    }
  }

  async removeManager(projectManagerId: string) {
    try {
      const { data, errors } = await this.client.models.ProjectManager.delete({
        id: projectManagerId
      });

      if (errors && errors.length) {
        console.error('Error removing manager:', errors);
        throw new Error(errors[0].message);
      }

      // Clear all manager cache since we don't know the projectId
      this.managersCache.clear();
      console.log('🗑️ [removeManager] Cleared all manager cache');

      // Real-time subscription will handle the refresh automatically
      console.log('✅ Manager removed, waiting for real-time update...');
      return data;
    } catch (e) {
      console.error('Failed to remove manager:', e);
      throw e;
    }
  }

  async getProjectManagers(projectId: string) {
    // Check cache first
    const cached = this.managersCache.get(projectId);
    const now = Date.now();

    if (cached && (now - cached.timestamp) < this.CACHE_TTL_MS) {
      console.log(`💾 [getProjectManagers] Using cached data for project ${projectId.substring(0, 8)}... (${cached.managers.length} managers)`);
      return cached.managers;
    }

    try {
      console.log(`🌐 [getProjectManagers] Fetching managers from server for project ${projectId.substring(0, 8)}...`);
      const { data, errors } = await this.client.models.ProjectManager.listManagersByProject(
        { projectId },
        {
          selectionSet: [
            'id',
            'projectId',
            'userId',
            'user.id',
            'user.name',
            'user.firstName',
            'user.email',
            'user.isArchived'
          ]
        }
      );

      if (errors && errors.length) {
        console.error('Error getting managers:', errors);
        throw new Error(errors[0].message);
      }

      const managers = data.map(pm => ({
        id: pm.id,
        projectId: pm.projectId,
        userId: pm.userId,
        user: pm.user ? {
          id: pm.user.id,
          name: pm.user.name,
          firstName: pm.user.firstName,
          email: pm.user.email,
          isArchived: pm.user.isArchived ?? false
        } : undefined
      }));

      // Update cache
      this.managersCache.set(projectId, { managers, timestamp: now });
      console.log(`💾 [getProjectManagers] Cached ${managers.length} managers for project ${projectId.substring(0, 8)}...`);

      return managers;
    } catch (e) {
      console.error('Failed to get managers:', e);
      return [];
    }
  }

  // Clear manager cache for a specific project (call after add/remove)
  clearManagerCache(projectId: string) {
    this.managersCache.delete(projectId);
    console.log(`🗑️ [clearManagerCache] Cleared cache for project ${projectId.substring(0, 8)}...`);
  }

  async isUserProjectManager(projectId: string, userId: string): Promise<boolean> {
    try {
      const { data } = await this.client.models.ProjectManager.list({
        filter: {
          and: [
            { projectId: { eq: projectId } },
            { userId: { eq: userId } }
          ]
        }
      });

      return data && data.length > 0;
    } catch (e) {
      console.error('Failed to check if user is manager:', e);
      return false;
    }
  }

  // PROJECT MEMBERS
  async addMember(projectId: string, userId: string) {
    try {
      // Check if member already exists
      const existing = await this.client.models.ProjectMember.list({
        filter: {
          and: [
            { projectId: { eq: projectId } },
            { userId: { eq: userId } }
          ]
        }
      });

      if (existing.data && existing.data.length > 0) {
        console.warn('User is already a member of this project');
        return existing.data[0];
      }

      const { data, errors } = await this.client.models.ProjectMember.create({
        projectId,
        userId
      });

      if (errors && errors.length) {
        console.error('Error adding member:', errors);
        throw new Error(errors[0].message);
      }

      // Clear cache for this project
      this.clearMemberCache(projectId);

      // Real-time subscription will handle the refresh automatically
      console.log('✅ Member added, waiting for real-time update...');
      return data;
    } catch (e) {
      console.error('Failed to add member:', e);
      throw e;
    }
  }

  async removeMember(projectMemberId: string) {
    try {
      const { data, errors } = await this.client.models.ProjectMember.delete({
        id: projectMemberId
      });

      if (errors && errors.length) {
        console.error('Error removing member:', errors);
        throw new Error(errors[0].message);
      }

      // Clear all member cache since we don't know the projectId
      this.membersCache.clear();
      console.log('🗑️ [removeMember] Cleared all member cache');

      // Real-time subscription will handle the refresh automatically
      console.log('✅ Member removed, waiting for real-time update...');
      return data;
    } catch (e) {
      console.error('Failed to remove member:', e);
      throw e;
    }
  }

  async getProjectMembers(projectId: string) {
    // Check cache first
    const cached = this.membersCache.get(projectId);
    const now = Date.now();

    if (cached && (now - cached.timestamp) < this.CACHE_TTL_MS) {
      console.log(`💾 [getProjectMembers] Using cached data for project ${projectId.substring(0, 8)}... (${cached.members.length} members)`);
      return cached.members;
    }

    try {
      console.log(`🌐 [getProjectMembers] Fetching members from server for project ${projectId.substring(0, 8)}...`);
      const { data, errors } = await this.client.models.ProjectMember.listMembersByProject(
        { projectId },
        {
          selectionSet: [
            'id',
            'projectId',
            'userId',
            'user.id',
            'user.name',
            'user.firstName',
            'user.email',
            'user.isArchived'
          ]
        }
      );

      if (errors && errors.length) {
        console.error('Error getting members:', errors);
        throw new Error(errors[0].message);
      }

      const members = data.map(pm => ({
        id: pm.id,
        projectId: pm.projectId,
        userId: pm.userId,
        user: pm.user ? {
          id: pm.user.id,
          name: pm.user.name,
          firstName: pm.user.firstName,
          email: pm.user.email,
          isArchived: pm.user.isArchived ?? false
        } : undefined
      }));

      // Update cache
      this.membersCache.set(projectId, { members, timestamp: now });
      console.log(`💾 [getProjectMembers] Cached ${members.length} members for project ${projectId.substring(0, 8)}...`);

      return members;
    } catch (e) {
      console.error('Failed to get members:', e);
      return [];
    }
  }

  // Clear member cache for a specific project (call after add/remove)
  clearMemberCache(projectId: string) {
    this.membersCache.delete(projectId);
    console.log(`🗑️ [clearMemberCache] Cleared cache for project ${projectId.substring(0, 8)}...`);
  }

  async isUserProjectMember(projectId: string, userId: string): Promise<boolean> {
    try {
      const { data } = await this.client.models.ProjectMember.list({
        filter: {
          and: [
            { projectId: { eq: projectId } },
            { userId: { eq: userId } }
          ]
        }
      });

      return data && data.length > 0;
    } catch (e) {
      console.error('Failed to check if user is member:', e);
      return false;
    }
  }

  async listProjects() {
    try {
      const listOptions: any = {
        selectionSet: [
          'id',
          'projectName',
          'startDate',
          'order',
          'estimatedHours',
          'status',
          'archivedAt',
          'tenantId',
          'links',
          'clientId',
          'client.id',
          'client.name',
          'tenant.id',
          'tenant.name',
          'managers.id',
          'managers.userId',
          'managers.user.id',
          'managers.user.name',
          'managers.user.firstName',
          'managers.user.email',
          'managers.user.isArchived',
          'members.id',
          'members.userId',
          'members.user.id',
          'members.user.name',
          'members.user.firstName',
          'members.user.email',
          'members.user.isArchived',
        'isDeleted'
        ],
        limit: 200,
      };
      // Always exclude soft-deleted; optionally merge with tenant filter
      const softDeleteFilter = { isDeleted: { ne: true } };
      if (this.tenantFilter) {
        listOptions.filter = { and: [softDeleteFilter, this.tenantFilter] };
      } else {
        listOptions.filter = softDeleteFilter;
      }

      // Paginate through all results
      const allProjects: Project[] = [];
      let nextToken: string | undefined = undefined;
      do {
        const result: any = await this.client.models.Project.list({
          ...listOptions,
          nextToken,
        });
        if (result.errors?.length) console.error('Error while getting projects', result.errors);
        const page = ((result.data || []) as any[]).map((p: any) => ({
          projectId: p.id,
          startDate: p.startDate ?? undefined,
          projectName: p.projectName,
          estimatedHours: (p as any).estimatedHours ?? 0,
          status: (p as any).status ?? null,
          archivedAt: (p as any).archivedAt ?? null,
          tenantId: p.tenantId,
          links: p.links?.filter((link: any) => link !== null).map((link: any) =>
            typeof link === 'string' ? JSON.parse(link) : link
          ) ?? undefined,
          order: p.order ?? 0,
          clientId: (p as any).clientId ?? undefined,
          client: (p as any).client ? { id: (p as any).client.id, name: (p as any).client.name } : undefined,
          tenant: p.tenant ? { id: p.tenant.id, name: p.tenant.name } : undefined,
          managers: p.managers?.map((pm: any) => ({
            id: pm.id,
            projectId: p.id,
            userId: pm.userId,
            user: pm.user ? {
              id: pm.user.id,
              name: pm.user.name,
              firstName: pm.user.firstName ?? undefined,
              email: pm.user.email ?? undefined,
              isArchived: pm.user.isArchived ?? false
            } : undefined
          })),
          members: p.members?.map((pm: any) => ({
            id: pm.id,
            projectId: p.id,
            userId: pm.userId,
            user: pm.user ? {
              id: pm.user.id,
              name: pm.user.name,
              firstName: pm.user.firstName ?? undefined,
              email: pm.user.email ?? undefined,
              isArchived: pm.user.isArchived ?? false
            } : undefined
          }))
        }));
        allProjects.push(...page);
        nextToken = result.nextToken ?? undefined;
      } while (nextToken);

      allProjects.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      this.projectsSubject.next(allProjects);
    } catch (e) {
      console.error('Failed to get all projects', e);
    }
  }

  /** One-off server-side query with optional tenant filter.
   *  Does NOT update the shared BehaviorSubject — safe for dashboard use. */
  async listProjectsFiltered(options: { tenantIds?: string[] } = {}): Promise<Project[]> {
    try {
      const softDeleteFilter = { isDeleted: { ne: true } };
      let filter: any = softDeleteFilter;
      if (options.tenantIds && options.tenantIds.length > 0) {
        const tenantFilter = options.tenantIds.length === 1
          ? { tenantId: { eq: options.tenantIds[0] } }
          : { or: options.tenantIds.map(id => ({ tenantId: { eq: id } })) };
        filter = { and: [softDeleteFilter, tenantFilter] };
      }
      const allData: any[] = [];
      let nextToken: string | undefined = undefined;
      do {
        const result: any = await this.client.models.Project.list({
          filter,
          selectionSet: [
            'id', 'projectName', 'startDate', 'order', 'estimatedHours', 'status', 'archivedAt', 'tenantId',
            'clientId', 'client.id', 'client.name', 'tenant.id', 'tenant.name', 'isDeleted'
          ],
          nextToken,
          limit: 200,
        });
        if (result.errors?.length) console.error('[ProjectService] listProjectsFiltered errors', result.errors);
        allData.push(...(result.data || []));
        nextToken = result.nextToken ?? undefined;
      } while (nextToken);
      return allData.map((p: any) => ({
        projectId: p.id,
        startDate: p.startDate ?? undefined,
        projectName: p.projectName,
        estimatedHours: p.estimatedHours ?? 0,
        status: p.status ?? null,
        archivedAt: p.archivedAt ?? null,
        tenantId: p.tenantId,
        order: p.order ?? 0,
        clientId: p.clientId ?? undefined,
        client: p.client ? { id: p.client.id, name: p.client.name } : undefined,
        tenant: p.tenant ? { id: p.tenant.id, name: p.tenant.name } : undefined,
      })) as Project[];
    } catch (e) {
      console.error('[ProjectService] listProjectsFiltered failed', e);
      return [];
    }
  }

  getEmployeesForProjectTasks(projectStages: ProjectStage[], tasks: Task[], employees: User[]): User[] {
    if (!projectStages.length || !tasks.length || !employees.length) return [];
    const stageIds = projectStages.map(s => s.projectStageId).filter(Boolean) as string[];
    const employeeIds = new Set<string>();
    tasks.filter(t => stageIds.includes(t.projectStageId))
      .forEach(task => {
        task.employeeId?.forEach(id => {
          if (id) employeeIds.add(id);
        });
      });
    return employees.filter(emp =>
      emp.id && employeeIds.has(emp.id)
    );
  }

  // Get all users who are members or managers of a project
  getProjectMemberUsers(project: Project | undefined, employees: User[]): User[] {
    if (!project || !employees.length) return [];

    const memberUserIds = new Set<string>();

    // Add manager user IDs
    project.managers?.forEach(manager => {
      if (manager.userId) memberUserIds.add(manager.userId);
    });

    // Add member user IDs
    project.members?.forEach(member => {
      if (member.userId) memberUserIds.add(member.userId);
    });

    // Return employees that match these IDs
    return employees.filter(emp => emp.id && memberUserIds.has(emp.id));
  }


  // PROJECTS STAGE
  async createProjectStage(projectStage: ProjectStage) {
    try {
      // Calculate next order for this project
      const currentStages = this.projectsStageSubject.value.filter(
        s => s.projectId === projectStage.projectId
      );
      const nextOrder = projectStage.order ?? currentStages.length;

      const { data, errors } = await this.client.models.ProjectStage.create({
        ...projectStage,
        order: nextOrder
      });
      if (errors && errors.length) console.error('Errors creating project stage', errors);
      console.log('Project stage created', data);
      this.listProjectsStage();
    } catch (e) {
      console.error('Failed when creating the project stage', e);
    }
  }

  async updateProjectStage(projectStageId: string, updates: Partial<ProjectStage>) {
    try {
      // Only include valid fields for the API
      const updateInput: any = { id: projectStageId };
      if (updates.stageName !== undefined) updateInput.stageName = updates.stageName;
      if (updates.color !== undefined) updateInput.color = updates.color;
      if (updates.startDate !== undefined) updateInput.startDate = updates.startDate;
      if (updates.endDate !== undefined) updateInput.endDate = updates.endDate;
      if (updates.status !== undefined) updateInput.status = updates.status;
      if (updates.projectId !== undefined) updateInput.projectId = updates.projectId;
      if (updates.order !== undefined) updateInput.order = updates.order;
      if (updates.estimatedHours !== undefined) updateInput.estimatedHours = updates.estimatedHours;
      if (updates.links !== undefined) {
        updateInput.links = updates.links.map((link: any) => JSON.stringify(link));
      }

      console.log('Updating stage with:', updateInput);

      const { data, errors } = await this.client.models.ProjectStage.update(updateInput, {
        selectionSet: ['id', 'projectId', 'stageName', 'color', 'startDate', 'endDate', 'status', 'order', 'estimatedHours', 'links', 'isDeleted']
      });
      if (errors && errors.length) {
        console.error('Errors updating project stage', errors);
        return null;
      }
      console.log('Stage updated successfully:', data);
      this.listProjectsStage();
      return data;
    } catch (e) {
      console.error('Failed to update the project stage', e);
      return null;
    }
  }

  async deleteProjectStage(stageId: string) {
    try {
      const now = new Date().toISOString();
      // Soft-delete the stage
      const { data, errors } = await this.client.models.ProjectStage.update({
        id: stageId,
        isDeleted: true,
        deletedAt: now
      });
      if (errors && errors.length) {
        console.error('Errors while soft-deleting stage', errors);
        return null;
      }
      // Cascade: soft-delete all tasks in this stage (paginated)
      let taskToken: string | undefined = undefined;
      do {
        const tasks: any = await this.client.models.Task.list({
          filter: { projectStageId: { eq: stageId }, isDeleted: { ne: true } },
          nextToken: taskToken,
          limit: 200,
        });
        for (const task of (tasks.data || [])) {
          await this.client.models.Task.update({
            id: task.id,
            isDeleted: true,
            deletedAt: now
          });
        }
        taskToken = tasks.nextToken ?? undefined;
      } while (taskToken);
      console.log('Stage soft-deleted (+ tasks)', data);
      this.listProjectsStage();
      return data;
    } catch (e) {
      console.error('Failed to soft-delete the stage', e);
      return null;
    }
  }

  async listProjectsStage() {
    try {
      const selectionSet = ['id', 'projectId', 'stageName', 'color', 'startDate', 'endDate', 'status', 'order', 'estimatedHours', 'links', 'isDeleted'];
      const allStages: ProjectStage[] = [];
      let nextToken: string | undefined = undefined;
      do {
        const result: any = await (this.client.models.ProjectStage.list as any)({
          filter: { isDeleted: { ne: true } },
          selectionSet,
          limit: 200,
          nextToken,
        });
        if (result.errors?.length) console.error('Error while getting project stages', result.errors);
        const page = (result.data || []).map((ps: any) => ({
          projectStageId: ps.id,
          projectId: ps.projectId,
          stageName: ps.stageName,
          color: ps.color,
          startDate: ps.startDate,
          endDate: ps.endDate,
          status: ps.status,
          order: ps.order ?? 0,
          estimatedHours: ps.estimatedHours ?? 0,
          links: ps.links?.filter((link: any) => link !== null).map((link: any) =>
            typeof link === 'string' ? JSON.parse(link) : link
          ) ?? undefined,
        }));
        allStages.push(...page);
        nextToken = result.nextToken ?? undefined;
      } while (nextToken);

      allStages.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      this.projectsStageSubject.next(allStages);
    } catch (e) {
      console.error('Failed to get all project stages', e);
    }
  }

  // PHASES MODEL
  async listAllPhaseList() {
    try {
      const { data: models } = await this.client.models.ModelPhase.list();
      const phaseList: PhaseModel[] = [];
      for (const pm of models) {
        const { data: phasesData } = await this.client.models.Phase.listPhasesByModelPhaseId({
          modelPhaseId: pm.id
        });

        phaseList.push({
          id: pm.id,
          modelName: pm.modelName,
          phases: phasesData.map((p) => ({
            id: p.id,
            name: p.name,
            color: p.color,
            order: p.order ?? 0,
            estimatedHours: p.estimatedHours ?? 0,
            links: p.links?.filter((link: any) => link !== null).map((link: any) =>
              typeof link === 'string' ? JSON.parse(link) : link
            ) ?? undefined
          }))
        });
      }
      this.phaseModelSubject.next(phaseList);
    } catch (e) { console.error('Error fetching phase models', e); }
  }

  async createPhaseList(phaseModel: PhaseModel) {
    try {
      const { data: modelData } = await this.client.models.ModelPhase.create({
        modelName: phaseModel.modelName
      });
      if (modelData && phaseModel.phases?.length) {
        // Create phases with their order preserved
        for (let i = 0; i < phaseModel.phases.length; i++) {
          const phase = phaseModel.phases[i];
          await this.client.models.Phase.create({
            name: phase.name,
            color: phase.color,
            estimatedHours: phase.estimatedHours ?? 0,
            order: i, // Set order based on position in array
            links: phase.links?.map((link: any) => JSON.stringify(link)) || undefined,
            modelPhaseId: modelData.id
          });
        }
      }
      this.listAllPhaseList();
    } catch (e) { console.error(e); }
  }

  async updatePhaseModel(modelId: string, updates: PhaseModel) {
    try {
      await this.client.models.ModelPhase.update({
        id: modelId,
        modelName: updates.modelName
      });
      const { data: currentDbPhases } = await this.client.models.Phase.listPhasesByModelPhaseId({
        modelPhaseId: modelId
      });
      const updatedIds = updates.phases.map(p => p.id).filter(id => !!id);
      const toDelete = currentDbPhases.filter(p => !updatedIds.includes(p.id));
      for (const phase of toDelete) {
        await this.client.models.Phase.delete({ id: phase.id });
      }
      for (let i = 0; i < updates.phases.length; i++) {
        const phase = updates.phases[i];
        if (phase.id) {
          await this.client.models.Phase.update({
            id: phase.id,
            name: phase.name,
            color: phase.color,
            estimatedHours: phase.estimatedHours ?? 0,
            order: i,
            links: phase.links?.map((link: any) => JSON.stringify(link)) || undefined,
            modelPhaseId: modelId
          });
        } else {
          await this.client.models.Phase.create({
            name: phase.name,
            color: phase.color,
            estimatedHours: phase.estimatedHours ?? 0,
            order: i,
            links: phase.links?.map((link: any) => JSON.stringify(link)) || undefined,
            modelPhaseId: modelId
          });
        }
      }
      this.listAllPhaseList();
    } catch (e) { console.error('Sync error', e); }
  }

  async deletePhaseModel(modelId: string) {
    try {
      const { data, errors } = await this.client.mutations.deletePhaseModelCustom({
        modelId
      });
      if (errors && errors.length) {
        console.error('Errors while deleting phase model via Lambda', errors);
        return null;
      }
      this.listAllPhaseList();
      return data;
    } catch (e) {
      console.error('Failed to call deletePhaseModelCustom', e);
      return null;
    }
  }

  resetState() {
    this.stopRealTimeRefresh();
    this.projectsSubject.next([]);
    this.projectsStageSubject.next([]);
    this.phaseModelSubject.next([]);
  }

  // REORDER METHODS
  async reorderProjectStages(stages: ProjectStage[]) {
    try {
      console.log('📝 Reordering project stages:', stages.map(s => ({ id: s.projectStageId, name: s.stageName, order: s.order })));

      const updatePromises = stages.map((stage, index) =>
        this.client.models.ProjectStage.update({
          id: stage.projectStageId!,
          order: index
        })
      );

      await Promise.all(updatePromises);
      console.log('✅ All stages updated in database');

      await this.listProjectsStage();
      return true;
    } catch (e) {
      console.error('❌ Failed to reorder project stages', e);
      return false;
    }
  }
}
